import * as _ from "lodash";
import * as React from "react";
import * as S from "@styled";
import { connect } from "react-redux";

import { addOrSelectTestPanel, toggleEditTask } from "../actions/SidePanelActions";
import { updateRetryCount } from "../actions/UpdateRetryCount";
import { updateSelectedTestTaskTypeId, updateSelectedTypeId } from "../actions/UpdateSelectedType";

import { IParameterSchema, ITaskData } from "../model/IProcessData";
import { State } from "../model/State";
import { ValidationError, ValidationErrorType } from "../model/ValidationData";

import { DispatchType } from "../../shared/utilities";
import { default as DescriptionEditor } from "./ElementDescriptionEditor";
import { hasValidationError } from "./Helpers";
import { default as LogResultEditor } from "./LogResultEditor";
import { default as DisposeEditor } from "./DisposeEditor";
import { default as PromoteResultEditor } from "./PromoteResultEditor";
import { RetryTaskEditor } from "./RetryTaskEditor";
import { SwitchCollapse } from "./SwitchCollapse";
import { default as TaskParameters } from "./TaskParameters";
import { default as TypeSelector, ITypeSelectorModel } from "./TypeSelector";
import { Option } from '../../shared/model/Option';

export const TaskEditor: React.FC<TaskEditorProps> = (props) => {

    React.useEffect(() => {
        if (!props.selectedTask && props.onNoSelectedTask) {
            props.onNoSelectedTask();
        }

        return () => props.onUnload?.()
    }, []);

    function onTaskSelect(taskId: string) {
        if (taskId) {
            const newSelectedTask = props.tasks.find((task) => task.uri === taskId);
            const schema = JSON.parse(newSelectedTask?.argumentsJson as string) as IParameterSchema[];
            const existingParameters = getExistingParameters(newSelectedTask);
            props.onTaskSelected(props.id, taskId, schema, existingParameters);
            props.onUnload?.();
        }
    }

    const handleCreateTest = () => {
        props.onCreateTest(props.existingParameters, props.selectedTask?.uri);
    };

    const getTaskSelectorValues = (): Option[] => {
        const options = props.taskSelectorModel.map((x) => x.options);
        const mergeOptions: Option[] = [].concat.apply([], options).map((x: Option) => ({ value: x.value, label: x.label }));
        return mergeOptions;
    };

    const newestTask = props?.taskSelectorModel?.find((f) => f?.options
        ?.find((s) => s?.deprecated === false && s?.metadata === props?.selectedTask?.name));

    const handleUpdateTask = () => {
        const newestTaskMethod = newestTask?.options?.find((f) => f.metadata === props.selectedTask?.name);
        const newSelectedTask = props.tasks.find((task) => task.uri === newestTaskMethod?.value);
        const schema = JSON.parse(newSelectedTask?.argumentsJson as string) as IParameterSchema[];
        const existingParameters = getExistingParameters(newSelectedTask);
        props.onTaskSelected(props.id, newestTaskMethod?.value, schema, existingParameters);
    };

    const getExistingParameters = (newSelectedTask: ITaskData | undefined) => {
        const existingParameters =
            // previous Task's name matches the new task's name
            (props.selectedTask && newSelectedTask?.name?.toLowerCase() === props.selectedTask.name.toLowerCase())
                // no previous Task selected (either a new task without parameters or a missing
                // Task with existing parameters which we want to be populated to the newly
                // selected task)
                || (!props.selectedTask && props.existingParameters)
                ? props.existingParameters : null;
        return existingParameters;
    }

    return (
        <div className="task-container-selector">
            {props.isObsoleteTask && (
                <S.WarningContainer>
                    <div className="alert-warning">
                        <div className="alert-label"><p><strong>{FRENDS.Localizations.Warning}</strong> {FRENDS.Localizations.Task_ObsoleteTask}</p></div>
                        <div className="alert-content">
                            <p>{props.selectedTask?.packageName}</p>
                            <p>{props.selectedTask?.name}</p>
                            {newestTask !== undefined ? (
                                <S.Button id="updateTaskToNewestButton" style={{ marginTop: "10px" }} onClick={handleUpdateTask}>
                                    {FRENDS.Localizations.Tasks_Update} {FRENDS.Localizations.Task}
                                </S.Button>
                            ) : (
                                // Task has been used in Process, but is now deleted from Tasks altogether
                                <p id="taskRemovedInformation" style={{ marginTop: "10px" }}>{FRENDS.Localizations.Task} is not installed. Install it from <a href="Task" target="_blank">Tasks <i className="ci-external_link" /></a>.</p>
                            )}
                        </div>
                    </div>
                </S.WarningContainer>
            )}
            {props.isNotCompatibleTask && (
                <S.ErrorContainer>
                    <div className="alert alert-warning" role="alert"><p><strong>{FRENDS.Localizations.Error}</strong> {FRENDS.Localizations.Task_NotCompatibleFramework} </p>
                        <p>{props.selectedTask?.packageName}</p>
                        <p>{props.selectedTask?.name}</p>
                    </div>
                </S.ErrorContainer>
            )}
            {props.shouldShowTaskSelector && (
                <TypeSelector
                    placeholder="Select a Task"
                    selectorModel={props.taskSelectorModel}
                    selectorValuesModel={getTaskSelectorValues()}
                    id={props.id}
                    onChange={onTaskSelect}
                    className={hasValidationError(props.validationErrors, ValidationErrorType.TaskSelectionMissing) ? "has-error row" : "row"}
                    searchMetadataProperty="metadata"
                />
            )}

            {!props.selectedTask ? null :
                <React.Fragment>
                    {props.isTest ? null :
                        <SwitchCollapse
                            isOpenByDefault={props.isAdvancedPanelOpenByDefault}
                            childrenCollapsed={<DescriptionEditor id={props.id} defaultDescription={(props.selectedTask || { description: "" }).description} showAsReadonly={true} />}
                            childrenOpen={
                                <S.InnerParameterFieldset>
                                    <S.FormGroup>
                                        <S.Button onClick={handleCreateTest}>{FRENDS.Localizations.CreateNew} {FRENDS.Localizations.Test.toLowerCase()} <i className="fa fa-flask" aria-hidden="true"></i></S.Button>
                                    </S.FormGroup>
                                    <DescriptionEditor id={props.id} defaultDescription={(props.selectedTask || { description: "" }).description} />
                                    <LogResultEditor elementId={props.id} />
                                    <DisposeEditor elementId={props.id} />
                                    <PromoteResultEditor id={props.id} />
                                    <RetryTaskEditor retryMaxCount={props.retryMaxCount} shouldRetry={props.shouldRetry} elementId={props.id} onRetryCountUpdate={props.onRetryUpdate} disabled={props.disabled} />
                                </S.InnerParameterFieldset>
                            }
                        />
                    }
                    <hr />
                    <TaskParameters
                        argumentJson={props.argumentJson}
                        existingParameters={props.existingParameters}
                        id={props.id}
                        taskKey={props.selectedTask.uri}
                    />
                </React.Fragment>}
        </div>
    );
};

interface TaskEditorOwnProps {
    id: string;
    isTest?: boolean;
    targetFramework?: string;
}

interface TaskEditorStateProps {
    tasks: ITaskData[];
    taskSelectorModel: ITypeSelectorModel[];
    selectedTask: ITaskData | undefined;
    retryMaxCount: number;
    shouldRetry: boolean;
    argumentJson: IParameterSchema[];
    isObsoleteTask: boolean;
    isNotCompatibleTask: boolean;
    validationErrors: ValidationError[];
    isAdvancedPanelOpenByDefault?: boolean;
    existingParameters: any;
    disabled?: boolean;
    shouldShowTaskSelector?: boolean;
}

interface TaskEditorDispatchProps {
    onTaskSelected: (id: string, taskId: string | undefined, schema: IParameterSchema[], currentParameters?: any) => void;
    onUpdateTask: (id: string, taskId: string | undefined, schema: IParameterSchema[], currentParameters?: any) => void;
    onRetryUpdate: (retries: number) => void;
    onCreateTest: (parameters: {}, selectedTaskId: string | undefined) => void;
    onUnload?: () => void;
    onNoSelectedTask?: () => void;
}

type TaskEditorProps = TaskEditorOwnProps & TaskEditorStateProps & TaskEditorDispatchProps;

export const getTasksMatchingSelectedFramework = (allTasks: ITaskData[], targetFx: string): ITaskDataWithCompatibilityInfo[] => {
    let tasks: ITaskDataWithCompatibilityInfo[];
    if (targetFx === "net471") {
        tasks = allTasks.filter((task, index, self) => {
            // First try to find the task targeting Framework.
            let foundIndex = self.findIndex((t) => (t.name === task.name && t.packageName === task.packageName && t.isDotNet === false && t.isStandard === false));
            // If no framework target found just take other one
            if (foundIndex === -1) {
                foundIndex = self.findIndex((t) => (t.name === task.name && t.packageName === task.packageName && t.isDotNet === false));
            }
            return foundIndex === index;
        });
    } else if (targetFx === "net6.0" || targetFx === "net8.0") {
        tasks = allTasks.filter((task, index, self) => {
            // First try to find the task targeting Dotnet.
            let foundIndex = self.findIndex((t) => (t.name === task.name && t.packageName === task.packageName && t.isDotNet && t.isStandard === false));
            // If no dotnet target found just take standard one
            if (foundIndex === -1) {
                foundIndex = self.findIndex((t) => (t.name === task.name && t.packageName === task.packageName && t.isStandard));
            }
            return foundIndex === index;
        });
    } else {
        tasks = allTasks.filter((task) => task.isStandard);
    }

    return tasks;
};

const mapStateToProps = (state: State, ownProps: TaskEditorOwnProps): TaskEditorStateProps => {
    const element = state.elements[ownProps.id];
    let argumentJson: IParameterSchema[] = [];
    let isObsoleteTask = false;
    let isNotCompatibleTask = false;
    let isAdvancedPanelOpenByDefault = false;
    let selectedTask: ITaskData | undefined;
    if (element.selectedTypeId) {
        selectedTask = state.tasks.find((task) => task.uri === element.selectedTypeId);
        if (selectedTask != null) {
            isObsoleteTask = selectedTask.isNotLatestVersion;
            argumentJson = JSON.parse(selectedTask.argumentsJson);
        }

        isAdvancedPanelOpenByDefault = element.shouldRetry || element.shouldNotLogResult || element.shouldDispose ||
            (_.has(element, "promoteResultAs") && element.promoteResultAs !== null);
    }

    const tasks = getTasksMatchingSelectedFramework(state.tasks, ownProps.targetFramework || state.process.targetFramework);
    if (selectedTask && !tasks.find((task) => task.uri === element.selectedTypeId)) {
        isNotCompatibleTask = true;
        const selectedTaskWithCompatibility = Object.assign({}, selectedTask, {
            isNotCompatible: true
        });
        tasks.push(selectedTaskWithCompatibility);
    }


    function createTypeSelectorModelForTasks(t: ITaskDataWithCompatibilityInfo[]) {
        const groupedByPackage = _.groupBy(t, (x) => x.packageName);
        return Object.keys(groupedByPackage)
            .map((packageKey) => {
                return {
                    label: packageKey,
                    options: groupedByPackage[packageKey]
                        .map((task) => ({
                            value: task.uri,
                            label: getTaskLabel(task),
                            metadata: task.name,
                            deprecated: task.isNotLatestVersion,
                            isNotCompatible: task.isNotCompatible,
                            title: getTaskTitle(task)
                        }))
                };
            });
    }

    // Show Task selector if editing or is new Task
    const isNewTask = !state.elements[ownProps.id].selectedTypeId;
    const shouldShowTaskSelector = state.uiState.isEditingTask || isNewTask || ownProps.isTest;

    return {
        shouldRetry: element.shouldRetry != null ? element.shouldRetry : false,
        retryMaxCount: element.maxRetryCount != null ? element.maxRetryCount : 1,
        tasks: state.tasks,
        taskSelectorModel: createTypeSelectorModelForTasks(tasks),
        argumentJson: argumentJson,
        isObsoleteTask: isObsoleteTask,
        isNotCompatibleTask: isNotCompatibleTask,
        selectedTask: selectedTask,
        validationErrors: state.validation.validationDictionary[ownProps.id],
        isAdvancedPanelOpenByDefault: isAdvancedPanelOpenByDefault,
        existingParameters: element.parameters,
        disabled: state.uiState.disableEdit,
        shouldShowTaskSelector
    };
};

export interface ITaskDataWithCompatibilityInfo extends ITaskData {
    isNotCompatible?: boolean;
}

function getTaskTitle(task: ITaskData) {
    let title = `${FRENDS.Localizations.Task}: ${task.name}\n`;
    if (task.packageName) {
        title += `${FRENDS.Localizations.Task_Package}: ${task.packageName}\n`;
    }
    if (task.description) {
        title += `${FRENDS.Localizations.Description}: ${task.description}\n`;
    }
    title += `${FRENDS.Localizations.Return} ${FRENDS.Localizations.Type}: ${task.returnType}`;

    return title;
}

export function getTaskLabel(task: ITaskData): string {
    if (task.displayName) {
        return task.displayName;
    }
    if (!task.name) { return ""; }
    const taskNameParts = task.name.split(".");
    return taskNameParts.splice(taskNameParts.length - 2).join(".");
}

const mapDispatchToProps = (dispatch: DispatchType<State>, settings: TaskEditorOwnProps): TaskEditorDispatchProps => {
    return {
        onTaskSelected: (id, taskId, schema, currentParameters) =>
            settings.isTest
                ? dispatch(updateSelectedTestTaskTypeId(id, taskId, schema))
                : dispatch(updateSelectedTypeId(id, taskId, schema, currentParameters)),
        onRetryUpdate: (retries: number) => dispatch(updateRetryCount(settings.id, retries)),
        onCreateTest: (parameters, selectedTaskId) => dispatch(addOrSelectTestPanel(parameters, selectedTaskId)),
        onUpdateTask: (id, taskId, schema, currentParameters) => dispatch(updateSelectedTypeId(id, taskId, schema, currentParameters)),
        onUnload: () => dispatch(toggleEditTask()),
        onNoSelectedTask: () => dispatch(toggleEditTask(true))
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(TaskEditor);